import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { BRAND_COLORS } from "./constants";
import { getCompanyDashboardPath } from "./paths";
import "./styles.css";

const SAMPLE_JOBS = [
	{ title: "Senior Software Engineer", location: "Dar es Salaam", type: "Full-time" },
	{ title: "Product Manager", location: "Dar es Salaam", type: "Full-time" },
	{ title: "Recruiter", location: "Remote", type: "Contract" },
	{ title: "Operations Lead", location: "Arusha", type: "Full-time" },
];

export default function SetupPage() {
	const navigate = useNavigate();
	const { companyName, branding, updateBranding } = useCompanyDashboard();
	const [step, setStep] = React.useState(1);
	const [selectedJobs, setSelectedJobs] = React.useState([0, 1, 2]);
	const maxSteps = 3;

	const next = () => {
		if (step >= maxSteps) {
			navigate(getCompanyDashboardPath("home"));
			return;
		}

		setStep((current) => current + 1);
	};

	const previous = () => {
		setStep((current) => Math.max(1, current - 1));
	};

	return (
		<div className="company-dashboard-app flex min-h-screen items-center justify-center bg-slate-50 p-6">
			<div className="w-full max-w-3xl overflow-hidden rounded-3xl border border-border bg-white shadow-xl">
				<div className="border-b border-border p-6">
					<div className="mb-4 flex items-center justify-between gap-3">
						<div>
							<h1 className="text-lg font-bold text-foreground">Set Up Your Company Dashboard</h1>
							<p className="text-sm text-muted-foreground">{companyName}</p>
						</div>
						<button
							type="button"
							className="text-sm text-muted-foreground hover:text-foreground"
							onClick={() => navigate(getCompanyDashboardPath("home"))}
						>
							Skip for now
						</button>
					</div>
					<div className="h-2 overflow-hidden rounded-full bg-secondary">
						<div
							className="company-dashboard-gradient h-full rounded-full transition-all duration-300"
							style={{ width: `${((step - 1) / (maxSteps - 1)) * 100}%` }}
						/>
					</div>
					<div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
						<span>
							Step {step} of {maxSteps}
						</span>
						<span>{Math.round(((step - 1) / (maxSteps - 1)) * 100)}% complete</span>
					</div>
				</div>

				<div className="p-8">
					{step === 1 ? (
						<div>
							<h2 className="text-center text-2xl font-bold text-foreground">Choose Banner Color</h2>
							<p className="mt-2 text-center text-sm text-muted-foreground">
								This color will be used on the company banner in the dashboard and public profile.
							</p>
							<div
								className="mt-8 h-48 w-full rounded-3xl"
								style={{
									background: `linear-gradient(to right, ${branding.primaryColor}99, ${branding.primaryColor})`,
								}}
							/>
							<div className="mt-6 flex flex-wrap justify-center gap-3">
								{BRAND_COLORS.map((color) => (
									<button
										key={color}
										type="button"
										onClick={() => updateBranding({ primaryColor: color })}
										className={`h-10 w-10 rounded-full transition-transform ${
											branding.primaryColor === color ? "scale-110 ring-2 ring-primary-500 ring-offset-2" : ""
										}`}
										style={{ backgroundColor: color }}
									/>
								))}
							</div>
							<div className="mx-auto mt-4 max-w-xs">
								<label className="mb-1 block text-sm font-medium text-foreground">Custom Color</label>
								<div className="flex items-center gap-3 rounded-2xl border border-input px-3 py-2">
									<input
										type="color"
										value={branding.primaryColor}
										onChange={(event) => updateBranding({ primaryColor: event.target.value })}
										className="h-10 w-10 cursor-pointer rounded border-0 p-0"
									/>
									<input
										type="text"
										value={branding.primaryColor.toUpperCase()}
										onChange={(event) => {
											const value = event.target.value;
											if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
												updateBranding({ primaryColor: value });
											}
										}}
										className="flex-1 bg-transparent text-sm font-mono focus:outline-hidden"
									/>
								</div>
							</div>
						</div>
					) : null}

					{step === 2 ? (
						<div>
							<h2 className="text-center text-2xl font-bold text-foreground">Tell Candidates About You</h2>
							<p className="mt-2 text-center text-sm text-muted-foreground">
								Write the company summary that should appear in the dashboard preview.
							</p>
							<textarea
								rows={7}
								value={branding.about}
								onChange={(event) => updateBranding({ about: event.target.value })}
								className="mt-8 w-full rounded-3xl border border-input px-4 py-3 text-sm focus:border-primary-500 focus:outline-hidden focus:ring-2 focus:ring-primary-100"
								placeholder="Describe what the company does, the mission, and what candidates can expect."
							/>
						</div>
					) : null}

					{step === 3 ? (
						<div>
							<h2 className="text-center text-2xl font-bold text-foreground">Review Company Jobs</h2>
							<p className="mt-2 text-center text-sm text-muted-foreground">
								Choose which roles you want top-of-mind while finishing setup.
							</p>
							<div className="mt-8 space-y-3">
								{SAMPLE_JOBS.map((job, index) => {
									const selected = selectedJobs.includes(index);
									return (
										<button
											key={job.title}
											type="button"
											onClick={() =>
												setSelectedJobs((current) =>
													selected ? current.filter((item) => item !== index) : [...current, index],
												)
											}
											className={`flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors ${selected ? "border-primary-300 bg-blue-50" : "border-border bg-white hover:bg-secondary/50"}`}
										>
											<div>
												<p className="text-sm font-semibold text-foreground">{job.title}</p>
												<p className="text-sm text-muted-foreground">
													{job.location} • {job.type}
												</p>
											</div>
											<div
												className={`flex h-7 w-7 items-center justify-center rounded-full ${selected ? "bg-primary-700 text-white" : "border border-border text-muted-foreground"}`}
											>
												{selected ? <Check className="h-4 w-4" /> : null}
											</div>
										</button>
									);
								})}
							</div>
						</div>
					) : null}
				</div>

				<div className="flex items-center justify-between border-t border-border p-6">
					<Button variant="outline" onClick={previous} disabled={step === 1}>
						<ChevronLeft className="mr-2 h-4 w-4" />
						Back
					</Button>
					<Button onClick={next}>
						{step === maxSteps ? "Finish Setup" : "Continue"}
						<ChevronRight className="ml-2 h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
